import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Send as SubmitIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError, showConfirmation } from '../../utils/sweetAlertConfig';
import CandidateHeader from '../../components/CandidateHeader';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const WARNING = "#f39c12";

const DigitalAssessmentNew = () => {
  const { attempt_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  useEffect(() => {
    loadExamQuestions();
  }, [attempt_id]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && exam) {
      handleAutoSubmit();
    }
  }, [timeLeft]);

  const loadExamQuestions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/candidate/exam-questions/${attempt_id}`);
      setExam(response.data.exam);
      setQuestions(response.data.questions);
      setTimeLeft(response.data.attempt.time_remaining * 60); // Convert to seconds
    } catch (error) {
      showError('Failed to load exam questions');
      navigate('/candidate/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleQuestionClick = (index) => {
    setCurrentQuestion(index);
  };

  const handleSubmitClick = () => {
    const answeredCount = Object.keys(answers).length;
    const totalQuestions = questions.length;

    if (answeredCount < totalQuestions) {
      setShowSubmitDialog(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setShowSubmitDialog(false);
    setSubmitting(true);

    try {
      // Here you would submit the answers to the backend
      // For now, we'll simulate the submission
      await new Promise(resolve => setTimeout(resolve, 2000));

      showSuccess('Assessment submitted successfully!', 'Your answers have been saved and are being evaluated.');
      navigate('/candidate/dashboard');
    } catch (error) {
      showError('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoSubmit = () => {
    showError('Time is up!', 'Your assessment has been automatically submitted.');
    handleSubmit();
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (timeLeft < 300) return 'error'; // Less than 5 minutes
    if (timeLeft < 600) return 'warning'; // Less than 10 minutes
    return 'primary';
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!exam || questions.length === 0) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Container>
          <Alert severity="error">Exam not found or has no questions</Alert>
        </Container>
      </ThemeProvider>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <ThemeProvider theme={candidateTheme}>
      <CandidateHeader />

      {/* Timer & Progress Bar */}
      <Paper
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderRadius: 0,
          boxShadow: 3,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" fontWeight={600}>
                {exam.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Question {currentQuestion + 1} of {questions.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} textAlign="right">
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <TimerIcon color={getTimeColor()} />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={getTimeColor()}
                >
                  {formatTime(timeLeft)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {answeredCount} of {questions.length} answered
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 2, height: 8, borderRadius: 4 }}
          />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Question Display */}
          <Grid item xs={12} md={9}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                {/* Question Number & Points */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Chip
                    label={`Question ${currentQuestion + 1}`}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${question.points} ${question.points === 1 ? 'Point' : 'Points'}`}
                    variant="outlined"
                  />
                </Box>

                {/* Question Text */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    bgcolor: '#f8f9fa',
                    borderLeft: `4px solid ${PRIMARY}`,
                  }}
                >
                  <Typography variant="h5" fontWeight={500} sx={{ lineHeight: 1.8 }}>
                    {question.question_text}
                  </Typography>
                </Paper>

                {/* Answer Options */}
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={answers[question.question_id] || ''}
                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                  >
                    {question.options && question.options.length > 0 ? (
                      question.options.map((option, idx) => {
                        const optionValue = option.option_id || option.option_text || `option_${idx}`;
                        const isSelected = answers[question.question_id] === optionValue;

                        return (
                          <Paper
                            key={option.option_id || idx}
                            variant="outlined"
                            onClick={() => handleAnswerChange(question.question_id, optionValue)}
                            sx={{
                              p: 3,
                              mb: 2,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              border: isSelected ? `3px solid ${PRIMARY}` : '2px solid #e0e0e0',
                              bgcolor: isSelected ? 'rgba(41, 32, 76, 0.05)' : 'white',
                              '&:hover': {
                                bgcolor: isSelected ? 'rgba(41, 32, 76, 0.08)' : '#f5f5f5',
                                borderColor: PRIMARY,
                                transform: 'translateX(4px)',
                              },
                            }}
                          >
                            <FormControlLabel
                              value={optionValue}
                              control={<Radio sx={{ mr: 2 }} />}
                              label={
                                <Typography
                                  variant="body1"
                                  fontWeight={isSelected ? 600 : 400}
                                  sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}
                                >
                                  {option.option_text}
                                </Typography>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Paper>
                        );
                      })
                    ) : question.question_type === 'true_false' ? (
                      <>
                        {['true', 'false'].map((value) => {
                          const isSelected = answers[question.question_id] === value;
                          return (
                            <Paper
                              key={value}
                              variant="outlined"
                              onClick={() => handleAnswerChange(question.question_id, value)}
                              sx={{
                                p: 3,
                                mb: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: isSelected ? `3px solid ${PRIMARY}` : '2px solid #e0e0e0',
                                bgcolor: isSelected ? 'rgba(41, 32, 76, 0.05)' : 'white',
                                '&:hover': {
                                  bgcolor: isSelected ? 'rgba(41, 32, 76, 0.08)' : '#f5f5f5',
                                  borderColor: PRIMARY,
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              <FormControlLabel
                                value={value}
                                control={<Radio sx={{ mr: 2 }} />}
                                label={
                                  <Typography
                                    variant="h6"
                                    fontWeight={isSelected ? 700 : 500}
                                  >
                                    {value === 'true' ? 'True' : 'False'}
                                  </Typography>
                                }
                                sx={{ width: '100%', m: 0 }}
                              />
                            </Paper>
                          );
                        })}
                      </>
                    ) : null}
                  </RadioGroup>
                </FormControl>

                {/* Navigation Buttons */}
                <Box display="flex" justifyContent="space-between" mt={4}>
                  <Button
                    variant="outlined"
                    onClick={handlePrevious}
                    disabled={currentQuestion === 0}
                    startIcon={<BackIcon />}
                  >
                    Previous
                  </Button>
                  {currentQuestion === questions.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmitClick}
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <SubmitIcon />}
                      sx={{ bgcolor: SUCCESS, '&:hover': { bgcolor: '#4a9c5a' } }}
                    >
                      Submit Assessment
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<NextIcon />}
                      sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1635' } }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Question Navigator */}
          <Grid item xs={12} md={3}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Question Navigator
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {questions.map((q, idx) => (
                    <Grid item xs={3} key={q.question_id}>
                      <Button
                        fullWidth
                        variant={idx === currentQuestion ? 'contained' : 'outlined'}
                        onClick={() => handleQuestionClick(idx)}
                        sx={{
                          minWidth: 40,
                          height: 40,
                          bgcolor: answers[q.question_id]
                            ? idx === currentQuestion
                              ? PRIMARY
                              : SUCCESS
                            : idx === currentQuestion
                            ? PRIMARY
                            : 'transparent',
                          color: answers[q.question_id] || idx === currentQuestion ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: idx === currentQuestion ? PRIMARY : answers[q.question_id] ? SUCCESS : undefined,
                          },
                        }}
                      >
                        {idx + 1}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box sx={{ width: 16, height: 16, bgcolor: SUCCESS, borderRadius: 1 }} />
                    <Typography variant="caption">Answered</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 16, height: 16, border: '1px solid #ccc', borderRadius: 1 }} />
                    <Typography variant="caption">Not Answered</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Incomplete Assessment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have answered {answeredCount} out of {questions.length} questions.
          </Typography>
          <Typography mt={2}>
            Are you sure you want to submit? Unanswered questions will be marked as incorrect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>Review Answers</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ bgcolor: WARNING, '&:hover': { bgcolor: '#e67e22' } }}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DigitalAssessmentNew;
